import jsPDF from 'jspdf';

export async function generateBIRFormPDF(
  formId: string,
  data: Record<string, string>,
): Promise<Uint8Array | null> {
  const pdf = new jsPDF('p', 'mm', 'a4');

  const FONT_NORMAL = 'times';
  const FONT_BOLD = 'times';
  const FONT_STYLE_BOLD = 'bold';
  const FONT_STYLE_NORMAL = 'normal';

  const pageWidth = 210;
  const margin = 20;
  let y = 17;

  const setBodyFont = () => {
    pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
    pdf.setFontSize(9);
  };

  // Helper to add centered text
  const addCenteredText = (
    text: string,
    fontSize: number,
    style: 'normal' | 'bold' = 'normal',
  ) => {
    pdf.setFont(
      FONT_BOLD,
      style === 'bold' ? FONT_STYLE_BOLD : FONT_STYLE_NORMAL,
    );
    pdf.setFontSize(fontSize);
    const textWidth = pdf.getTextWidth(text);
    pdf.text(text, (pageWidth - textWidth) / 2, y);
    y += fontSize * 0.3 + 1.5;
  };

  // Helper to add wrapped paragraph
  const addParagraph = (text: string, indent = 0) => {
    setBodyFont();
    const lines = pdf.splitTextToSize(text, pageWidth - margin * 2 - indent);
    pdf.text(lines, margin + indent, y);
    y += lines.length * 4.2 + 2;
  };

  if (formId === 'b1' || formId === 'b2') {
    const isB1 = formId === 'b1';

    // ANNEX
    pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
    pdf.setFontSize(10);
    pdf.text(isB1 ? 'ANNEX "B-1"' : 'ANNEX "B-2"', pageWidth - margin - 25, y);
    y += 6;

    // Title
    addCenteredText(
      'INCOME PAYEE’S SWORN DECLARATION OF GROSS RECEIPTS/SALES',
      11,
      'bold',
    );
    addCenteredText(
      isB1
        ? '(For Self-Employed and/or Engaged in the Practice of Profession with Several Income Payors)'
        : '(For Self-Employed and/or Engaged in the Practice of Profession with Lone Income Payor)',
      10,
      'bold',
    );
    y += 5;

    // Opening
    const spousePart = data.name_of_spouse
      ? `, married to ${data.name_of_spouse}`
      : '';
    const openingText = `I, ${data.taxpayer_name || '__________'}, ${data.citizenship || '__________'}, of legal age${spousePart}, permanently residing at ${data.address || '__________'} with Taxpayer Identification Number (TIN) ${data.tin || '__________'}, after having been duly sworn in accordance with law hereby depose and state:`;
    addParagraph(openingText);

    // Numbered Points
    if (isB1) {
      addParagraph(
        `1. That I derived my ${data.busines_professional || '__________'} income from various income payors, and my registered business address is at ${data.business_address || '__________'};`,
        5,
      );
    } else {
      addParagraph(
        `1. That I derived my ${data.busines_professional || '__________'} income from lone income payor ${data.name_of_lone_payer || '__________'} with TIN ${data.lone_payer_tin || '__________'} and registered business address is at ${data.lone_payer_address || '__________'};`,
        5,
      );
    }

    addParagraph(
      `2. That for the current year ${data.current_year || '__________'}, my gross receipts will not exceed Three Million Pesos (P3,000,000) and that I am a non-VAT registered taxpayer. For this purpose, I opt to avail of either one of the income tax regime as follows:`,
      5,
    );

    // Regimes
    const isGraduated = data.tax_rate === 'graduated_income';
    const isEightPercent = data.tax_rate === 'eight_percent';

    // Graduated checkbox
    pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
    pdf.rect(margin + 10, y, 3.5, 3.5);
    if (isGraduated) pdf.text('X', margin + 10.8, y + 2.8);
    pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
    pdf.setFontSize(9);
    const gradText = `Graduated Income Tax Rates under Section ${data.section || '24(A)(2)(a)'} of the Tax Code, as amended, based on the taxable income. With this selection, I acknowledge that I am subject to creditable withholding tax at the prescribed rate; subject to percentage tax and will file the required percentage tax returns or subject to withholding percentage tax, in case of government money payments.`;
    const gradLines = pdf.splitTextToSize(
      gradText,
      pageWidth - margin * 2 - 20,
    );
    pdf.text(gradLines, margin + 20, y + 2.8);
    y += gradLines.length * 4.2 + 2;

    // 8% checkbox
    pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
    pdf.rect(margin + 10, y, 3.5, 3.5);
    if (isEightPercent) pdf.text('X', margin + 10.8, y + 2.8);
    pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
    const eightText = `Eight Percent (8%) income tax rate under Section 24(A)(2)(b) of the Tax Code, as amended, based on gross receipts/sales and other non-operating income - with this selection, I understand that this is in lieu of the graduated income tax rates and the Percentage Tax under Section 116 of the Tax Code, as amended; thus, only the creditable income withholding tax based on the prescribed rate shall be made;`;
    const eightLines = pdf.splitTextToSize(
      eightText,
      pageWidth - margin * 2 - 20,
    );
    pdf.text(eightLines, margin + 20, y + 2.8);
    y += eightLines.length * 4.2 + 6;

    addParagraph(
      `3. That based on my selection above, if my gross sales/receipts and other non-operating income exceeds P3,000,000, my income payor /withholding agents shall automatically withhold the higher rate of withholding of ten percent (10%) in the case of income items with two (2) prescribed creditable withholding tax rate depending on the total amount of income payment received:`,
      5,
    );
    addParagraph(
      `a. In case of Graduated Income Tax Rates, I acknowledge that aside from income tax, I am subject to business tax (VAT) unless expressly exempted; and consequently subject to withholding of income. Moreover, if the payor is a government entity, business tax withholding applies; OR`,
      10,
    );
    addParagraph(
      `b. In case of Eight Percent (8%) income tax rate, I am no longer qualified to avail of this option since my income exceeds P3,000,000 and thus, the graduated income tax rates above shall automatically apply together with the consequent liability for business tax/es;`,
      10,
    );

    addParagraph(
      `4. That I duly execute this SWORN DECLARATION in compliance with the requirement prescribed under Section ${data.section || '__________'} of Revenue Regulations No. ${data.revenue_regulations_no || '11-2018'};`,
      5,
    );
    addParagraph(
      `5. That I declare, under the penalties of perjury, that this declaration has been made in good faith, and to the best of my knowledge and belief to be true and correct.`,
      5,
    );

    y += 1;
    const witnessPrefix = 'IN WITNESS WHEREOF, ';
    const witnessText = `I have hereunto set my hand this ${data.hereunto_day || '____'} day of ${data.hereunto_month || '__________'}, 20${data.hereunto_year || '____'} at ${data.hereunto_location || '__________'}, Philippines`;

    pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
    pdf.setFontSize(10);
    const prefixWidth = pdf.getTextWidth(witnessPrefix);
    pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
    const mainTextWidth = pdf.getTextWidth(witnessText);
    const totalWitnessWidth = prefixWidth + mainTextWidth;

    const witnessStartX = (pageWidth - totalWitnessWidth) / 2;
    pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
    pdf.text(witnessPrefix, witnessStartX, y);
    pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
    pdf.text(witnessText, witnessStartX + prefixWidth, y);

    y += 10;
    const sigX = pageWidth - margin - 70;
    const sigWidth = 60;
    pdf.line(sigX, y, sigX + sigWidth, y);
    y += 3;
    pdf.setFontSize(6);
    const sigLabel1 = 'Signature over Printed Name of Individual Taxpayer';
    const sigLabel1Width = pdf.getTextWidth(sigLabel1);
    pdf.text(sigLabel1, sigX + (sigWidth - sigLabel1Width) / 2, y);
    y += 2;
  } else if (formId === 'b3') {
    // Annex B-3 for Non-Individual
    pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
    pdf.setFontSize(10);
    pdf.text('ANNEX "B-3"', pageWidth - margin - 25, y);
    y += 6;

    addCenteredText(
      'NON-INDIVIDUAL INCOME PAYEE’S SWORN DECLARATION OF GROSS RECEIPTS/SALES',
      11,
      'bold',
    );
    y += 4;

    const openingText = `I, ${data.taxpayer_name || '__________'}, ${data.citizenship || '__________'}, of legal age, representing ${data.name_of_non_individual_payee || '__________'} with Taxpayer Identification Number (TIN) ${data.tin || '__________'} and registered address at ${data.address || '__________'}, after having been duly sworn in accordance with law hereby depose and state:`;
    addParagraph(openingText);

    addParagraph(
      `1. That for the current year ${data.current_year || '__________'}, the gross receipts of the said entity will not exceed Three Million Pesos (P3,000,000) and that it is a non-VAT registered taxpayer.`,
      5,
    );
    addParagraph(
      `2. That this SWORN DECLARATION is executed in compliance with the requirement prescribed under Section ${data.section || '__________'} of Revenue Regulations No. ${data.revenue_regulations_no || '11-2018'};`,
      5,
    );
    addParagraph(
      `3. That I declare, under the penalties of perjury, that this declaration has been made in good faith, and to the best of my knowledge and belief to be true and correct.`,
      5,
    );

    y += 1;
    const witnessPrefix = 'IN WITNESS WHEREOF, ';
    const witnessText = `I have hereunto set my hand this ${data.hereunto_day || '____'} day of ${data.hereunto_month || '__________'}, 20${data.hereunto_year || '____'} at ${data.hereunto_location || '__________'}, Philippines`;

    pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
    pdf.setFontSize(10);
    const prefixWidth = pdf.getTextWidth(witnessPrefix);
    pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
    const mainTextWidth = pdf.getTextWidth(witnessText);
    const totalWitnessWidth = prefixWidth + mainTextWidth;

    const witnessStartX = (pageWidth - totalWitnessWidth) / 2;
    pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
    pdf.text(witnessPrefix, witnessStartX, y);
    pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
    pdf.text(witnessText, witnessStartX + prefixWidth, y);

    y += 12;
    const sigX = pageWidth - margin - 70;
    const sigWidth = 60;
    pdf.line(sigX, y, sigX + sigWidth, y);
    y += 3;
    pdf.setFontSize(8);
    const sigLabel2 =
      'Signature over Printed Name of Authorized Representative';
    const sigLabel2Width = pdf.getTextWidth(sigLabel2);
    pdf.text(sigLabel2, sigX + (sigWidth - sigLabel2Width) / 2, y);
  }

  // Notary section (common)
  y = y + 10;
  pdf.setFontSize(9);
  pdf.text(
    'SUBSCRIBED AND SWORN to before me this ____ day of _______________, 20____ in ____________________.',
    margin,
    y,
  );
  y += 5;
  pdf.text(
    'Applicant exhibited to me his/her ___________________________ issued at _________________________ on _____________________.',
    margin,
    y,
  );
  y += 3.5;
  pdf.setFontSize(7);
  pdf.text('(Government Issued ID and No.)', margin + 40, y);

  y += 6;
  addCenteredText('NOTARY PUBLIC', 10);

  // Notary Details (Left) - Compact
  const notaryDetailsY = y + 1;
  pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
  pdf.setFontSize(6);
  pdf.text('Doc. No.: __________', margin, notaryDetailsY);
  pdf.text('Page No.: __________', margin, notaryDetailsY + 3.2);
  pdf.text('Book No.: __________', margin, notaryDetailsY + 6.4);
  pdf.text('Series of ___________', margin, notaryDetailsY + 9.6);

  // Documentary Stamp Box
  y = notaryDetailsY + 12;
  (pdf as any).setLineDash([1, 1], 0);
  pdf.rect(margin, y, 22, 12);
  (pdf as any).setLineDash([], 0);
  pdf.setFontSize(6.5);
  pdf.text('Affix P30.00', margin + 2, y + 4);
  pdf.text('Documentary', margin + 2, y + 7);
  pdf.text('Stamp Tax', margin + 2, y + 10);

  // Footer Section (Withholding Agent) - Dynamic Following
  y += 18;
  pdf.setLineWidth(0.4);
  pdf.line(margin, y, pageWidth - margin, y);
  pdf.setLineWidth(0.1);
  pdf.line(margin, y + 0.5, pageWidth - margin, y + 0.5);
  y += 4;
  addCenteredText(
    '(To be filled-out by the withholding agent/lone payor)',
    8,
    'bold',
  );

  y += 6;
  pdf.setFont(FONT_BOLD, FONT_STYLE_BOLD);
  pdf.setFontSize(9);
  pdf.text('Date Received:', margin, y);
  pdf.text('Received by:', margin + 80, y);

  y += 4;
  pdf.setFont(FONT_NORMAL, FONT_STYLE_NORMAL);
  pdf.text('__________________', margin, y);
  const footerLineX = margin + 80;
  const footerLineW = pageWidth - margin - footerLineX;
  pdf.line(footerLineX, y, pageWidth - margin, y);
  y += 3;
  pdf.setFontSize(8);
  pdf.text('(MM-DD-YYYY-00001)', margin, y);

  pdf.setFontSize(6);
  const footerSigLabel =
    'Signature over Printed Name of the Withholding Agent/Payor or Authorized Officer';
  const footerSigWidth = pdf.getTextWidth(footerSigLabel);
  pdf.text(footerSigLabel, footerLineX + (footerLineW - footerSigWidth) / 2, y);

  y += 6;
  pdf.line(footerLineX, y, pageWidth - margin, y);
  y += 3;
  const desigLabel = 'Designation/Position of Authorized Officer';
  const desigWidth = pdf.getTextWidth(desigLabel);
  pdf.text(desigLabel, footerLineX + (footerLineW - desigWidth) / 2, y);

  y += 6;
  pdf.line(footerLineX, y, pageWidth - margin, y);
  y += 3;
  const nameLabel = 'Name of Withholding Agent/Lone Payor';
  const nameWidth = pdf.getTextWidth(nameLabel);
  pdf.text(nameLabel, footerLineX + (footerLineW - nameWidth) / 2, y);

  return new Uint8Array(pdf.output('arraybuffer'));
}
